import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  type Timestamp,
  type Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from '../services/firebase';
import type { ApiResponse } from '../types/api';
import httpClient from './http/httpClient';
import type { TenderFileDownloadResponse, UploadDraftResponse } from './tenderService';
export interface TenderDocument {
  uploadedAt: Timestamp | Date | string;
  doc_type?: string;
  [key: string]: unknown;
}

export const subscribeToTenderContextDocs = (
  tenderId: string,
  userId: string,
  hasViewAllFiles: boolean,

  callback: (docs: Record<string, unknown>[]) => void,
  onError: (error: Error) => void
): Unsubscribe => {
  let contextDocsQuery: import('firebase/firestore').Query = query(
    collection(firestore, `tenders/${tenderId}/tender_docs`),
    orderBy('uploadedAt', 'desc')
  );

  if (!hasViewAllFiles) {
    contextDocsQuery = query(
      collection(firestore, `tenders/${tenderId}/tender_docs`),
      where('uploaderId', '==', userId),
      orderBy('uploadedAt', 'desc')
    );
  }

  return onSnapshot(
    contextDocsQuery,

    (snapshot: import('firebase/firestore').QuerySnapshot) => {
      const docsData = snapshot.docs.map(
        (doc: import('firebase/firestore').QueryDocumentSnapshot) => ({
          id: doc.id,
          ...doc.data(),
        })
      );
      callback(docsData);
    },
    onError
  );
};

export const uploadTenderDraft = async (
  tenderId: string,
  draftFile: File
): Promise<UploadDraftResponse> => {
  // Draft uploads might not need encryption in all cases, but if the system is encrypted, it likely should.
  const formData = new FormData();
  formData.append('draftFile', draftFile);
  formData.append('tenderId', tenderId);

  const response = await httpClient.post<UploadDraftResponse>('/uploadTenderDraft', formData);
  return response;
};

export const getTenderFileDownloadUrl = async (gsURI: string): Promise<string> => {
  const response = await httpClient.post<TenderFileDownloadResponse>('/getTenderFileDownloadUrl', {
    gsURI,
  });

  if (response.success) {
    return response.downloadUrl;
  }

  throw new Error(response.message || 'Failed to get download URL from API.');
};

export async function uploadSupportDocForTender({
  userId,
  tenderId,
  file,
  clientId,
  actualBucketName,
  docType,
  docCategory,
}: {
  userId: string;
  tenderId: string;
  file: File;
  clientId: string;
  actualBucketName: string;
  docType?: string;
  docCategory?: string;
}) {
  if (!userId || !tenderId || !file || !clientId || !actualBucketName) {
    throw new Error('Missing required parameters for support document upload.');
  }

  try {
    const response = await httpClient.post<{ success: boolean; message?: string }>(
      '/uploadSupportDocForTender',
      file,
      {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-User-ID': userId,
          'X-Tender-ID': tenderId,
          'X-Client-ID': clientId,
          'X-Bucket-Name': actualBucketName,
          'X-File-Name': file.name,
          'X-File-Mime-Type': file.type || 'application/octet-stream',
          ...(docType ? { 'X-Doc-Type': docType } : {}),
          ...(docCategory ? { 'X-Doc-Category': docCategory } : {}),
        },
        timeout: 0,
      }
    );

    if (response?.success) {
      return response;
    } else {
      throw new Error(response.message || 'Support document upload failed.');
    }
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Support document upload failed: ${err.message}`);
  }
}

// Upload draft for review for tender using storage abstraction
export async function uploadDraftForTenderReview({
  userId,
  tenderId,
  file,
  clientId,
  actualBucketName,
}: {
  userId: string;
  tenderId: string;
  file: File;
  clientId: string;
  actualBucketName: string;
}) {
  if (!userId || !tenderId || !file || !clientId || !actualBucketName) {
    throw new Error('Missing required parameters for draft for review upload.');
  }

  try {
    const response = await httpClient.post<{ success: boolean; message?: string }>(
      '/uploadDraftForTenderReview',
      file,
      {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-User-ID': userId,
          'X-Tender-ID': tenderId,
          'X-Client-ID': clientId,
          'X-Bucket-Name': actualBucketName,
          'X-File-Name': file.name,
          'X-File-Mime-Type': file.type || 'application/octet-stream',
        },
      }
    );

    if (response?.success) {
      return response;
    } else {
      throw new Error(response.message || 'Draft for review upload failed.');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: unknown } };
    if (err.response?.data) {
      throw err.response.data;
    }
    throw new Error(`Draft for review upload failed: ${err.message}`);
  }
}

// Upload tender form using storage abstraction
export async function uploadTenderForm({
  userId,
  tenderId,
  file,
  clientId,
  actualBucketName,
  isStrictForm,
}: {
  userId: string;
  tenderId: string;
  file: File;
  clientId: string;
  actualBucketName: string;
  isStrictForm?: boolean | undefined;
}) {
  if (!userId || !tenderId || !file || !clientId || !actualBucketName) {
    throw new Error('Missing required parameters for tender form upload.');
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/octet-stream',
      'X-User-ID': userId,
      'X-Tender-ID': tenderId,
      'X-Client-ID': clientId,
      'X-Bucket-Name': actualBucketName,
      'X-File-Name': file.name,
      'X-File-Mime-Type': file.type || 'application/octet-stream',
    };
    if (isStrictForm !== undefined) {
      headers['X-Is-Strict-Form'] = String(isStrictForm);
    }

    const response = await httpClient.post<{ success: boolean; message?: string }>(
      '/uploadTenderForm',
      file,
      { headers }
    );

    if (response?.success) {
      return response;
    } else {
      throw new Error(response.message || 'Tender form upload failed.');
    }
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Tender form upload failed: ${err.message}`);
  }
}

// Upload tender CV using storage abstraction
export async function uploadTenderCV({
  userId,
  tenderId,
  file,
  clientId,
  actualBucketName,
  docType,
}: {
  userId: string;
  tenderId: string;
  file: File;
  clientId: string;
  actualBucketName: string;
  docType?: string;
}) {
  if (!userId || !tenderId || !file || !clientId || !actualBucketName) {
    throw new Error('Missing required parameters for tender CV upload.');
  }

  try {
    const response = await httpClient.post<{ success: boolean; message?: string }>(
      '/uploadTenderCV',
      file,
      {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-User-ID': userId,
          'X-Tender-ID': tenderId,
          'X-Client-ID': clientId,
          'X-Bucket-Name': actualBucketName,
          'X-File-Name': file.name,
          'X-File-Mime-Type': file.type || 'application/octet-stream',
          ...(docType ? { 'X-Doc-Type': docType } : {}),
        },
      }
    );

    if (response?.success) {
      return response;
    } else {
      throw new Error(response.message || 'Tender CV upload failed.');
    }
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Tender CV upload failed: ${err.message}`);
  }
}

export const deleteTenderForm = async (params: {
  userId: string;
  clientId: string;
  tenderId: string;
  fileName: string;
  originalFilename?: string;
  docId?: string;
}) => {
  const response = await httpClient.delete<ApiResponse<{ message: string }>>(
    '/deleteTenderForm',
    params
  );

  if (!response.success) {
    throw new Error(response.message || 'Delete failed');
  }

  return response;
};

// Fetch all support docs for a tender
export async function fetchSupportDocsForTender({
  userId,
  tenderId,
  clientId,
}: {
  userId: string;
  tenderId: string;
  clientId: string;
}) {
  if (!userId || !tenderId || !clientId) {
    throw new Error('Missing required parameters for fetching support documents.');
  }
  const subcolRef = collection(firestore, 'tenders', tenderId, 'library');
  const querySnapshot = await getDocs(subcolRef);
  return querySnapshot.docs
    .map((docSnap) => docSnap.data() as TenderDocument)
    .sort((a: TenderDocument, b: TenderDocument) => {
      // Handle different uploadedAt formats (Firestore Timestamp, Date, or string)
      const getTimestamp = (uploadedAt: Timestamp | Date | string | undefined) => {
        if (!uploadedAt) {
          return 0;
        }
        if (uploadedAt && typeof uploadedAt === 'object' && 'seconds' in uploadedAt) {
          // Firestore Timestamp
          return uploadedAt.seconds * 1000;
        }
        if (typeof uploadedAt === 'string' || uploadedAt instanceof Date) {
          return new Date(uploadedAt).getTime();
        }
        return 0;
      };

      return getTimestamp(b.uploadedAt) - getTimestamp(a.uploadedAt);
    });
}

// Fetch all drafts for review for a tender (sorted by uploadedAt desc)
export async function fetchDraftsForTenderReview({
  userId,
  tenderId,
  clientId,
}: {
  userId: string;
  tenderId: string;
  clientId: string;
}) {
  if (!userId || !tenderId || !clientId) {
    throw new Error('Missing required parameters for fetching drafts for review.');
  }
  const subcolRef = collection(firestore, 'tenders', tenderId, 'draftsForReview');
  const querySnapshot = await getDocs(subcolRef);
  return querySnapshot.docs
    .map((docSnap) => docSnap.data() as TenderDocument)
    .sort((a: TenderDocument, b: TenderDocument) => {
      // Handle different uploadedAt formats (Firestore Timestamp, Date, or string)
      const getTimestamp = (uploadedAt: Timestamp | Date | string | undefined) => {
        if (!uploadedAt) {
          return 0;
        }
        if (uploadedAt && typeof uploadedAt === 'object' && 'seconds' in uploadedAt) {
          // Firestore Timestamp
          return uploadedAt.seconds * 1000;
        }
        if (typeof uploadedAt === 'string' || uploadedAt instanceof Date) {
          return new Date(uploadedAt).getTime();
        }
        return 0;
      };

      return getTimestamp(b.uploadedAt) - getTimestamp(a.uploadedAt);
    });
}

// New function to delete a draft for tender review
export async function deleteDraftForTenderReview({
  userId,
  clientId,
  tenderId,
  generatedFileName,
  actualBucketName,
}: {
  userId: string;
  clientId: string;
  tenderId: string;
  generatedFileName: string;
  actualBucketName: string;
}) {
  if (!userId || !clientId || !tenderId || !generatedFileName || !actualBucketName) {
    throw new Error('Missing required parameters for deleting draft for review.');
  }

  try {
    // Call the backend deleteDraftForTenderReview function which uses storage abstraction
    const response = await httpClient.delete<{ success: boolean; message?: string }>(
      '/deleteDraftForTenderReview',
      {
        userId,
        clientId,
        tenderId,
        generatedFileName,
      }
    );

    if (!response?.success) {
      throw new Error(response?.message || 'Delete failed.');
    }

    // File deleted successfully
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Draft deletion failed: ${err.message}`);
  }
}

// New function to update document category in Firestore
export async function updateDocumentCategory({
  clientId,
  userId,
  tenderId,
  docName,
  docType,
  isDraft,
  role,
  specialization,
}: {
  clientId: string;
  userId: string;
  tenderId: string;
  docName: string;
  docType?: string | undefined;
  isDraft?: boolean | undefined;
  role?: string | undefined;
  specialization?: string | undefined;
}) {
  if (!clientId || !userId || !tenderId || !docName) {
    throw new Error('Missing required parameters for updating document category.');
  }

  // Determine the correct subcollection based on whether it's a draft or a support document
  const subcollection = isDraft ? 'draftsForReview' : 'library';
  const firestoreDocPath = ['tenders', tenderId, subcollection, docName].join('/');
  const fileDocRef = doc(firestore, firestoreDocPath);

  try {
    const updateData: Record<string, string | boolean | undefined> = {
      doc_type: docType, // Update or add the doc_type field
    };

    if (role !== undefined) {
      updateData.role = role;
    }
    if (specialization !== undefined) {
      updateData.specialization = specialization;
    }

    await updateDoc(fileDocRef, updateData);
    return { success: true, message: `Category updated to ${docType || 'none'}.` };
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Failed to update document category: ${err.message}`);
  }
}

// Missing functions needed by tests

export const uploadAssociatedTenderFile = async (params: {
  tenderId: string;
  file: File;
  uploadCategory: string;
}) => {
  const response = await httpClient.post<{
    success: boolean;
    message?: string;
    uploadUrl?: string;
    storageUri?: string;
    gcsPath?: string; // Deprecated
  }>('/getSignedUrlForAssociatedTenderFile', {
    tenderId: params.tenderId,
    fileName: params.file.name,
    fileSize: params.file.size,
    contentType: params.file.type,
  });

  if (!response.success) {
    throw new Error(response.message || 'Failed to get signed URL');
  }

  if (!response.uploadUrl) {
    throw new Error('Failed to get upload URL');
  }

  // Upload to signed URL
  await fetch(response.uploadUrl, {
    method: 'PUT',
    body: params.file,
    headers: {
      'Content-Type': params.file.type,
    },
  });

  return {
    success: true,
    storageUri: response.storageUri || response.gcsPath,
    gcsPath: response.gcsPath,
    message: 'File uploaded to GCS successfully.',
  };
};

export const deleteSupportDocForTender = async (params: {
  userId: string;
  clientId: string;
  tenderId: string;
  fileName: string;
  docCategory?: string;
  docId?: string;
}) => {
  const response = await httpClient.delete<ApiResponse<{ message: string }>>(
    '/deleteSupportDocForTender',
    params
  );

  if (!response.success) {
    throw new Error(response.message || 'Delete failed');
  }

  return response;
};
